import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { FormNavigation } from '../components/form/FormNavigation';
import { RadioGroup } from '../components/fields/RadioGroup';
import { AddressAutocomplete } from '../components/fields/AddressAutocomplete';
import { useFormStore, useNavigation } from '../store/formStore';
import { addressStepSchema } from '../utils/validation';

interface AddressFormData {
  country: string;
  city: string;
  street: string;
  postalCode: string;
}

export function AddressStep() {
  const { formData, updateField, errors } = useFormStore();
  const { goToPreviousStep, goToNextStep, canGoBack } = useNavigation();
  
  const {
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors: formErrors, isValid, touchedFields }
  } = useForm<AddressFormData>({
    resolver: zodResolver(addressStepSchema),
    defaultValues: {
      country: formData.country || 'FR',
      city: formData.city || '',
      street: formData.street || '',
      postalCode: formData.postalCode || ''
    },
    mode: 'onChange'
  });
  
  useEffect(() => { trigger(); }, []);

  const fieldError = (name: keyof AddressFormData) =>
    touchedFields[name] ? formErrors[name]?.message : undefined;

  const watchedCountry = watch('country');
  const watchedCity = watch('city');
  const watchedStreet = watch('street');
  const watchedPostalCode = watch('postalCode');
  
  const countryOptions = [
    { value: 'FR', label: 'France' },
    { value: 'MC', label: 'Monaco' }
  ];
  
  const onSubmit = (data: AddressFormData) => {
    // Mettre à jour toutes les données dans le store
    Object.entries(data).forEach(([key, value]) => {
      updateField(key, value);
    });
    
    goToNextStep();
  };
  
  const handleCountryChange = (value: string) => {
    setValue('country', value, { shouldValidate: true, shouldTouch: true });
    updateField('country', value);

    // Reset les champs et pré-remplir selon le pays
    setValue('street', '', { shouldValidate: true, shouldTouch: true });
    updateField('street', '');

    if (value === 'MC') {
      setValue('city', 'MONACO', { shouldValidate: true, shouldTouch: true });
      updateField('city', 'MONACO');
      setValue('postalCode', '98000', { shouldValidate: true, shouldTouch: true });
      updateField('postalCode', '98000');
    } else {
      setValue('city', '', { shouldValidate: true, shouldTouch: true });
      updateField('city', '');
      setValue('postalCode', '', { shouldValidate: true, shouldTouch: true });
      updateField('postalCode', '');
    }
  };

  // Gérer la sélection d'adresse complète
  const handleAddressSelect = (addressData: any) => {
    setValue('street', addressData.address, { shouldValidate: true, shouldTouch: true });
    updateField('street', addressData.address);

    // Toujours synchroniser ville et code postal depuis l'adresse sélectionnée
    if (addressData.city) {
      setValue('city', addressData.city, { shouldValidate: true, shouldTouch: true });
      updateField('city', addressData.city);
    }
    if (addressData.postcode) {
      setValue('postalCode', addressData.postcode, { shouldValidate: true, shouldTouch: true });
      updateField('postalCode', addressData.postcode);
    }
  };
  
  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Votre adresse</CardTitle>
          <CardDescription>
            Passons maintenant à votre adresse de résidence
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Pays */}
            <div>
              <RadioGroup
                label="Dans quel pays habitez-vous ?"
                options={countryOptions}
                value={watchedCountry}
                onChange={handleCountryChange}
                name="country"
                required
                error={fieldError('country') || errors.country}
              />
            </div>

            {/* Adresse */}
            <div>
              {watchedCountry === 'MC' ? (
                <div className="field-group">
                  <label className="form-label">
                    N°, type et nom de la voie
                    <span className="form-label-required">*</span>
                  </label>
                  <input
                    type="text"
                    value={watchedStreet}
                    onChange={(e) => {
                      setValue('street', e.target.value, { shouldValidate: true, shouldTouch: true });
                      updateField('street', e.target.value);
                    }}
                    onBlur={() => setValue('street', watchedStreet, { shouldTouch: true, shouldValidate: true })}
                    placeholder="Ex: 1 Avenue de Monte-Carlo"
                    className={`input-field ${fieldError('street') || errors.street ? 'input-error' : ''}`}
                  />
                  {(fieldError('street') || errors.street) && (
                    <p className="error-message">{fieldError('street') || errors.street}</p>
                  )}
                </div>
              ) : (
                <AddressAutocomplete
                  value={watchedStreet}
                  onChange={(value) => {
                    setValue('street', value, { shouldValidate: true });
                    updateField('street', value);
                  }}
                  onAddressSelect={handleAddressSelect}
                  onBlur={() => trigger('street')}
                  label="N°, type et nom de la voie"
                  placeholder="Ex: 123 rue de la Paix"
                  required
                  error={fieldError('street') || errors.street}
                />
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Ville (lecture seule, rempli via l'adresse) */}
              <div className="field-group">
                <label className="form-label">
                  Ville
                  <span className="form-label-required">*</span>
                </label>
                <input
                  type="text"
                  value={watchedCity}
                  disabled
                  className="input-field bg-neutral-100 text-neutral-600 cursor-not-allowed"
                  placeholder="Rempli automatiquement"
                />
              </div>

              {/* Code postal (lecture seule, rempli via l'adresse) */}
              <div className="field-group">
                <label className="form-label">
                  Code postal
                  <span className="form-label-required">*</span>
                </label>
                <input
                  type="text"
                  value={watchedPostalCode}
                  disabled
                  className="input-field bg-neutral-100 text-neutral-600 cursor-not-allowed"
                  placeholder="Rempli automatiquement"
                />
              </div>
            </div>

            {/* Informations selon le pays */}
            {watchedCountry === 'FR' && (
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                <h3 className="font-semibold text-primary-800 mb-2">
                  🇫🇷 Résidence en France
                </h3>
                <p className="text-sm text-primary-700">
                  Votre demande sera traitée selon la réglementation française. 
                  Assurez-vous que votre adresse est complète et à jour.
                </p>
              </div>
            )}
            
            {watchedCountry === 'MC' && (
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                <h3 className="font-semibold text-primary-800 mb-2">
                  🇲🇨 Résidence à Monaco
                </h3>
                <p className="text-sm text-primary-700">
                  Des conditions spécifiques peuvent s'appliquer pour les résidents monégasques.
                </p>
              </div>
            )}
            
            <FormNavigation
              onPrevious={goToPreviousStep}
              onNext={handleSubmit(onSubmit)}
              canGoBack={canGoBack}
              canGoNext={isValid}
              nextButtonText="Continuer"
            />
          </form>
        </CardContent>
      </Card>
    </div>
  );
}